import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CardListConfig } from '../../crud-view/model/card-data-map-config';
import { cloneDeep } from 'lodash';
import { ActionBehavior } from '../../../utils/action-utils';
import { AlertTypes } from '../../../utils/alert/alert-types';
import { TextComponentConfig } from '../../../utils/text';
import { SelectableOptionWithTooltip } from '../../../utils/form-utils/selectable-option.type';
import { IconType, IconComponentConfig } from '../../../utils/icon';
import { SelectMode, SelectSettings } from '../../../utils/select';
import { OptionMode } from '../../../utils/select/select-option-mode.enum';
import { RowSelectColumn } from '../../../utils/row-select/row-select-column.interface';
import { ButtonComponentConfig } from '../../../utils/button/button-component-config.interface';
import { ButtonType } from '../../../utils/button/button-type.enum';
import { NzTabSetComponent } from 'ng-zorro-antd/tabs';
import { buttonTypeSelectOptions } from '../../../utils/shared/button/button-type-select-options';
import { PopConfirmPlacement } from '../../../utils/button/pop-confirm-placement.enum';
import { Action, isActionEqual } from '../../../utils/action-utils';
import { ActionsInputComponent } from '../../grid-builder/actions-input/actions-input.component';

@Component({
  selector: 'rappider-card-builder-settings',
  templateUrl: './card-builder-settings.component.html',
  styleUrls: ['./card-builder-settings.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CardBuilderSettingsComponent),
      multi: true
    }
  ]
})
export class CardBuilderSettingsComponent implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('cardSettingsTabs') cardSettingsTabs: NzTabSetComponent;
  @ViewChild('editActionsInput') editActionsInput: ActionsInputComponent;
  @ViewChild('deleteActionsInput') deleteActionsInput: ActionsInputComponent;

  @Input() entityFields: SelectableOptionWithTooltip[] = [];
  @Input() apiPath: string;
  @Output() ngModelChange = new EventEmitter<CardListConfig>();
  @Output() editJsonData = new EventEmitter<void>();

  activeTabIndex = 0;

  // Select component settings
  SelectMode = SelectMode;
  OptionMode = OptionMode;
  selectSettings: SelectSettings = {
    allowClear: true,
    searchable: true
  };

  // Row select columns configuration for subtitle field names
  subtitleRowSelectColumns: RowSelectColumn[] = [
    {
      fieldName: 'fieldName',
      visible: true,
      unique: true,
      selectConfig: {
        options: [],
        placeholder: 'Select field for subtitle',
        settings: {
          allowClear: true,
          searchable: true
        }
      }
    }
  ];

  // Row select button configurations
  subtitleRowSelectAddButton: ButtonComponentConfig = {
    text: 'Add Field',
    icon: { name: 'fas fa-plus', type: IconType.FontAwesome },
    type: ButtonType.Default
  };

  subtitleRowSelectEditButton: ButtonComponentConfig = {
    text: 'Edit',
    icon: { name: 'far fa-edit', type: IconType.FontAwesome },
    type: ButtonType.Default
  };

  subtitleRowSelectCancelButton: ButtonComponentConfig = {
    text: 'Cancel',
    type: ButtonType.Default
  };

  subtitleRowSelectSaveButton: ButtonComponentConfig = {
    text: 'Save',
    type: ButtonType.Primary
  };

  subtitleRowSelectValueProperty: any[] = [];

  useSubtitleFunction = false;
  useDescriptionFunction = false;
  descriptionFunctionString = '';
  subtitleFunctionString = '';
  tempDescriptionFieldName: string | undefined;
  tempSubtitleFieldNames: string[] | undefined;

  // Monaco Editor options
  monacoEditorOptions = {
    language: 'javascript',
    minimap: { enabled: false },
    lineNumbers: 'on',
    folding: true,
    automaticLayout: true
  };

  CardBuilderEmptyInfoAlertConfig = {
    type: AlertTypes.Info,
    description: <TextComponentConfig>{
      text: 'Configure card view settings'
    },
    closeable: false,
    showIcon: true
  };

  ActionBehavior = ActionBehavior;
  buttonTypeSelectOptions = buttonTypeSelectOptions;
  behaviorSelectOptions: SelectableOptionWithTooltip[] = Object.entries(ActionBehavior).map(([key, value]) => ({ key: key, value: value }));
  popConfirmPlacementTypeSelectableOption: SelectableOptionWithTooltip[] = Object.entries(PopConfirmPlacement).map(([key, value]) => ({ key: key, value: value }));

  // Actions array for actions-input components
  editActions: Action[] = [];
  deleteActions: Action[] = [];

  _value: CardListConfig = {
    titleFieldName: '',
    descriptionFieldName: '',
    subtitleFieldNames: [],
    cardItemClickBehavior: ActionBehavior.CrudDetailLink,
    showSearchWithAiButton: false
  };
  actionBehaviorOptions: SelectableOptionWithTooltip[] = Object.entries(ActionBehavior)
    .filter(([, value]) => value !== ActionBehavior.DropDownMenu)
    .map(([key, value]) => {
      const formattedKey = key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());

      // Tooltip descriptions for each action behavior
      const tooltipDescriptions: Record<string, string> = {
        'External Route': 'Opens detail page in a new browser tab',
        'Route': 'Navigates to detail page using Angular routing',
        'Emit': 'Triggers a custom event when clicked',
        'Crud Detail Link': 'Shows item details in the same view (recommended)',
        'Crud Edit Link': 'Shows item edit form in the same view',
        'Crud Delete Link': 'Shows item delete confirmation in the same view',
        'Crud Create Link': 'Shows item creation form in the same view'
      };

      return {
        key: formattedKey,
        value: value,
        tooltipText: tooltipDescriptions[formattedKey] || formattedKey
      };
    });

  constructor() { }

  get showEditItemActionButton(): boolean {
    return !!this._value?.editItemActionButton;
  }

  get showDeleteItemActionButton(): boolean {
    return !!this._value?.deleteItemActionButton;
  }

  get value() {
    return this._value;
  }

  set value(value: CardListConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.ngModelChange.emit(value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.entityFields) {
      this.updateSubtitleRowSelectOptions();
      if (this._value?.subtitleFieldNames) {
        this.updateSubtitleRowSelectValueProperty();
      }
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CardListConfig) {
    if (!value) {
      this._value = {
        titleFieldName: '',
        descriptionFieldName: '',
        subtitleFieldNames: [],
        cardItemClickBehavior: ActionBehavior.CrudDetailLink,
        showSearchWithAiButton: false
      };
      this.useSubtitleFunction = false;
      this.useDescriptionFunction = false;
      this.descriptionFunctionString = '';
      this.subtitleFunctionString = '';
      this.tempDescriptionFieldName = undefined;
      this.tempSubtitleFieldNames = undefined;
    } else {
      this._value = cloneDeep(value);
      if (!this._value.subtitleFieldNames) {
        this._value.subtitleFieldNames = [];
      }
      const restoredValue = this.restoreFunctions(this._value);
      this._value = restoredValue;
      this.useSubtitleFunction = !!(this._value.getSubTitlesFunction || this._value.subtitleFunctionString);
      this.useDescriptionFunction = !!(this._value.getDescriptionFunction || this._value.descriptionFunctionString);

      if (this._value.descriptionFunctionString) {
        this.descriptionFunctionString = this._value.descriptionFunctionString;
      } else if (this._value.getDescriptionFunction && typeof this._value.getDescriptionFunction === 'function') {
        this.descriptionFunctionString = this._value.getDescriptionFunction.toString();
      } else {
        this.descriptionFunctionString = '';
      }

      if (this._value.subtitleFunctionString) {
        this.subtitleFunctionString = this._value.subtitleFunctionString;
      } else if (this._value.getSubTitlesFunction && typeof this._value.getSubTitlesFunction === 'function') {
        this.subtitleFunctionString = this._value.getSubTitlesFunction.toString();
      } else {
        this.subtitleFunctionString = '';
      }

      this.updateSubtitleRowSelectOptions();
    }
    this.updateSubtitleRowSelectValueProperty();
    this.updateActionsArrays();
  }

  /**
   * Update actions arrays from value
   */
  private updateActionsArrays() {
    if (this.value?.editItemActionButton) {
      this.editActions = [{ ...this.value.editItemActionButton }];
    } else {
      this.editActions = [];
    }

    if (this.value?.deleteItemActionButton) {
      this.deleteActions = [{ ...this.value.deleteItemActionButton }];
    } else {
      this.deleteActions = [];
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onEditJsonData() {
    this.editJsonData.emit();
  }

  /**
   * Update subtitle row select column options with current entity fields
   */
  updateSubtitleRowSelectOptions() {
    if (this.subtitleRowSelectColumns && this.subtitleRowSelectColumns.length > 0) {
      const currentColumn = this.subtitleRowSelectColumns[0];
      this.subtitleRowSelectColumns = [
        {
          ...currentColumn,
          selectConfig: {
            ...currentColumn.selectConfig,
            options: [...(this.entityFields || [])]
          }
        }
      ];
    }
  }

  /**
   * Handle subtitle row select value change
   */
  onSubtitleRowSelectChange(value: any[]) {
    if (!value || !Array.isArray(value)) {
      this.value = { ...this.value, subtitleFieldNames: [] };
      this.subtitleRowSelectValueProperty = [];
      return;
    }
    const fieldNames = value
      .map(item => item?.fieldName)
      .filter(fieldName => fieldName && typeof fieldName === 'string');
    this.value = { ...this.value, subtitleFieldNames: fieldNames };
    this.subtitleRowSelectValueProperty = cloneDeep(value);
  }

  /**
   * Handle title field name change
   */
  onTitleFieldNameChange(value: string) {
    this.value = { ...this.value, titleFieldName: value || '' };
  }

  /**
   * Handle description field name change
   */
  onDescriptionFieldNameChange(value: string) {
    this.value = { ...this.value, descriptionFieldName: value || '' };
  }

  /**
   * Handle subtitle function mode toggle
   */
  onUseSubtitleFunctionChange(useFunction: boolean) {
    this.useSubtitleFunction = useFunction;
    if (!useFunction) {
      this.value = { ...this.value, getSubTitlesFunction: undefined };
      this.subtitleFunctionString = '';
      if (this.tempSubtitleFieldNames !== undefined) {
        this.value = { ...this.value, subtitleFieldNames: [...this.tempSubtitleFieldNames] };
        this.updateSubtitleRowSelectValueProperty();
        this.tempSubtitleFieldNames = undefined;
      }
    } else {
      if (this.value.subtitleFieldNames && this.value.subtitleFieldNames.length > 0) {
        this.tempSubtitleFieldNames = [...this.value.subtitleFieldNames];
      }
      this.value = { ...this.value, subtitleFieldNames: [] };
      this.subtitleRowSelectValueProperty = [];
      if (!this.subtitleFunctionString) {
        const fields = this.getAvailableFieldNames();
        const field1 = fields[0] || 'field1';
        const field2 = fields[1] || 'field2';
        this.subtitleFunctionString =
          '(item) => {\n' +
          '  const result = [];\n' +
          `  // Check if ${field1} field exists and add to subtitles\n` +
          `  if (item.${field1}) {\n` +
          `    const value = item.${field1};\n` +
          '    // If value is an object, display it using JSON viewer\n' +
          '    if (typeof value === \'object\' && value !== null && !Array.isArray(value)) {\n' +
          '      result.push({ content: value, type: \'h6\' });\n' +
          '    } else {\n' +
          '      // If value is primitive, display with icon\n' +
          '      result.push({ content: `<i class=\'fa-regular fa-circle\'></i> ${value}`, type: \'h6\' });\n' +
          '    }\n' +
          '  }\n' +
          `  // Check if ${field2} field exists and add to subtitles\n` +
          `  if (item.${field2}) {\n` +
          `    const value = item.${field2};\n` +
          '    // If value is an object, display it using JSON viewer\n' +
          '    if (typeof value === \'object\' && value !== null && !Array.isArray(value)) {\n' +
          '      result.push({ content: value, type: \'h6\' });\n' +
          '    } else {\n' +
          '      // If value is primitive, display with icon\n' +
          '      result.push({ content: `<i class=\'fa-regular fa-circle\'></i> ${value}`, type: \'h6\' });\n' +
          '    }\n' +
          '  }\n' +
          '  return result;\n' +
          '}';
      }
    }
    this.onItemsChange();
  }

  /**
   * Handle description function mode toggle
   */
  onUseDescriptionFunctionChange(useFunction: boolean) {
    this.useDescriptionFunction = useFunction;
    if (!useFunction) {
      this.value = { ...this.value, getDescriptionFunction: undefined };
      this.descriptionFunctionString = '';
      if (this.tempDescriptionFieldName !== undefined) {
        this.value = { ...this.value, descriptionFieldName: this.tempDescriptionFieldName };
        this.tempDescriptionFieldName = undefined;
      }
    } else {
      if (this.value.descriptionFieldName) {
        this.tempDescriptionFieldName = this.value.descriptionFieldName;
      }
      this.value = { ...this.value, descriptionFieldName: undefined };
      if (!this.descriptionFunctionString) {
        const fields = this.getAvailableFieldNames();
        const descriptionField = this.entityFields?.find(f =>
          f.value?.toLowerCase().includes('description') ||
          f.key?.toLowerCase().includes('description')
        )?.value || fields[0] || 'description';
        this.descriptionFunctionString =
          '(item) => {\n' +
          `  // Get ${descriptionField} field value\n` +
          `  const value = item.${descriptionField};\n` +
          '  // Return null if value is empty\n' +
          '  if (!value) return null;\n' +
          '  // If value is an object, return it for JSON viewer display\n' +
          '  if (typeof value === \'object\' && value !== null && !Array.isArray(value)) {\n' +
          '    return value;\n' +
          '  }\n' +
          '  // Return primitive value as string\n' +
          '  return value;\n' +
          '}';
      }
    }
    this.onItemsChange();
  }

  /**
   * Handle subtitle function code change
   */
  onSubtitleFunctionChange(value: string) {
    // Allow empty string - user can clear the entire function
    this.subtitleFunctionString = value ?? '';
    this.onItemsChange();
  }

  /**
   * Handle description function code change
   */
  onDescriptionFunctionChange(value: string) {
    // Allow empty string - user can clear the entire function
    this.descriptionFunctionString = value ?? '';
    this.onItemsChange();
  }

  /**
   * Handle card item click behavior change
   */
  onCardItemClickBehaviorChange(value: ActionBehavior) {
    const updatedValue = { ...this.value, cardItemClickBehavior: value };

    if (value === ActionBehavior.Route) {
      const basePath = this.apiPath || 'undefined';
      if (!updatedValue.cardItemRouterLink || updatedValue.cardItemRouterLink.includes('/undefined/')) {
        updatedValue.cardItemRouterLink = `/${basePath}/detail/{{id}}`;
      }
    } else {
      delete updatedValue.cardItemRouterLink;
    }

    this.value = updatedValue;
  }

  /**
   * Handle card item router link change
   */
  onCardItemRouterLinkChange(value: string) {
    this.value = { ...this.value, cardItemRouterLink: value || '' };
  }

  /**
   * Handle icon config change
   */
  onIconConfigChange(iconConfig: IconComponentConfig | null) {
    if (iconConfig) {
      this.value = { ...this.value, iconConfig: iconConfig };
    } else {
      const newValue = { ...this.value };
      delete newValue.iconConfig;
      this.value = newValue;
    }
  }

  /**
   * Handle show search with AI button change
   */
  onShowSearchWithAiButtonChange(value: boolean) {
    this.value = { ...this.value, showSearchWithAiButton: value };
  }

  /**
   * Handle show edit item action button toggle
   */
  onShowEditItemActionButtonChange(show: boolean) {
    if (!show) {
      this.value = { ...this.value, editItemActionButton: undefined };
      this.editActions = [];
    } else {
      if (!this.value.editItemActionButton) {
        const defaultEditAction: Action = {
          name: 'edit',
          text: 'Edit',
          icon: { name: 'fa-regular fa-pen', type: IconType.FontAwesome },
          behavior: ActionBehavior.Emit,
          tooltipText: 'Edit',
          emitWithoutPopconfirm: true
        };
        this.value = {
          ...this.value,
          editItemActionButton: defaultEditAction
        };
        this.editActions = [defaultEditAction];
      } else {
        this.editActions = [{ ...this.value.editItemActionButton }];
      }
    }
    this.onItemsChange();
  }

  /**
   * Handle show delete item action button toggle
   */
  onShowDeleteItemActionButtonChange(show: boolean) {
    if (!show) {
      this.value = { ...this.value, deleteItemActionButton: undefined };
      this.deleteActions = [];
    } else {
      if (!this.value.deleteItemActionButton) {
        const defaultDeleteAction: Action = {
          name: 'delete',
          text: 'Delete',
          icon: { name: 'fa-regular fa-trash', type: IconType.FontAwesome },
          behavior: ActionBehavior.Emit,
          tooltipText: 'Delete',
          emitWithoutPopconfirm: true
        };
        this.value = {
          ...this.value,
          deleteItemActionButton: defaultDeleteAction
        };
        this.deleteActions = [defaultDeleteAction];
      } else {
        this.deleteActions = [{ ...this.value.deleteItemActionButton }];
      }
    }
    this.onItemsChange();
  }

  /**
   * Handle edit actions change from actions-input
   */
  onEditActionsChange(actions: Action[]) {
    if (actions && actions.length > 0) {
      const action = actions[0];
      const currentAction = this.value?.editItemActionButton;
      if (!isActionEqual(currentAction, action)) {
        this.value = {
          ...this.value,
          editItemActionButton: action
        };
        requestAnimationFrame(() => {
          this.onItemsChange();
        });
      }
    } else {
      if (this.value?.editItemActionButton) {
        this.value = { ...this.value, editItemActionButton: undefined };
        requestAnimationFrame(() => {
          this.onItemsChange();
        });
      }
    }
  }

  /**
   * Handle delete actions change from actions-input
   */
  onDeleteActionsChange(actions: Action[]) {
    if (actions && actions.length > 0) {
      const action = actions[0];
      const currentAction = this.value?.deleteItemActionButton;
      if (!isActionEqual(currentAction, action)) {
        this.value = {
          ...this.value,
          deleteItemActionButton: action
        };
        requestAnimationFrame(() => {
          this.onItemsChange();
        });
      }
    } else {
      if (this.value?.deleteItemActionButton) {
        this.value = { ...this.value, deleteItemActionButton: undefined };
        requestAnimationFrame(() => {
          this.onItemsChange();
        });
      }
    }
  }

  /**
   * Handle edit action visibility toggle
   */
  onEditActionVisibilityToggle(event: { index: number; visible: boolean }) {
    if (this.value.editItemActionButton) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.value.editItemActionButton as any).visible = event.visible;
      if (this.editActions && this.editActions.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.editActions[0] as any).visible = event.visible;
      }
      this.onItemsChange();
    }
  }

  /**
   * Handle delete action visibility toggle
   */
  onDeleteActionVisibilityToggle(event: { index: number; visible: boolean }) {
    if (this.value.deleteItemActionButton) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.value.deleteItemActionButton as any).visible = event.visible;
      if (this.deleteActions && this.deleteActions.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.deleteActions[0] as any).visible = event.visible;
      }
      this.onItemsChange();
    }
  }

  /**
   * Handle edit action custom delete (remove)
   */
  onEditActionCustomDelete(_index: number) {
    this.value = { ...this.value, editItemActionButton: undefined };
    this.editActions = [];
    this.onItemsChange();
  }

  /**
   * Handle delete action custom delete (remove)
   */
  onDeleteActionCustomDelete(_index: number) {
    this.value = { ...this.value, deleteItemActionButton: undefined };
    this.deleteActions = [];
    this.onItemsChange();
  }

  ngAfterViewInit() {
    if (this.cardSettingsTabs) {
      this.cardSettingsTabs.nzSelectedIndexChange.subscribe((index) => {
        this.activeTabIndex = index;
      });
    }
  }

  ngOnDestroy(): void {
    // Component cleanup if needed
  }

  /**
   * Set active tab programmatically
   * @param index Tab index to set as active
   */
  setActiveTab(index: number) {
    this.activeTabIndex = index;
    if (this.cardSettingsTabs) {
      this.cardSettingsTabs.nzSelectedIndex = index;
    }
  }


  /**
   * Handle items change - similar to grid builder
   */
  onItemsChange() {
    const updatedValue = { ...this.value };

    // Convert description function string to function
    if (this.useDescriptionFunction) {
      if (this.descriptionFunctionString && this.descriptionFunctionString.trim()) {
        try {
          // eslint-disable-next-line no-eval
          updatedValue.getDescriptionFunction = eval(`(${this.descriptionFunctionString})`);
          updatedValue.descriptionFunctionString = this.descriptionFunctionString;
        } catch (error) {
          // If eval fails, keep the function string but don't set the function
          updatedValue.descriptionFunctionString = this.descriptionFunctionString;
          updatedValue.getDescriptionFunction = undefined;
        }
      } else {
        // Empty string - clear the function
        updatedValue.getDescriptionFunction = undefined;
        updatedValue.descriptionFunctionString = '';
      }
    } else if (!this.useDescriptionFunction) {
      updatedValue.getDescriptionFunction = undefined;
      updatedValue.descriptionFunctionString = undefined;
    }

    // Convert subtitle function string to function
    if (this.useSubtitleFunction) {
      if (this.subtitleFunctionString && this.subtitleFunctionString.trim()) {
        try {
          // eslint-disable-next-line no-eval
          updatedValue.getSubTitlesFunction = eval(`(${this.subtitleFunctionString})`);
          updatedValue.subtitleFunctionString = this.subtitleFunctionString;
        } catch (error) {
          // If eval fails, keep the function string but don't set the function
          updatedValue.subtitleFunctionString = this.subtitleFunctionString;
          updatedValue.getSubTitlesFunction = undefined;
        }
      } else {
        // Empty string - clear the function
        updatedValue.getSubTitlesFunction = undefined;
        updatedValue.subtitleFunctionString = '';
      }
    } else if (!this.useSubtitleFunction) {
      updatedValue.getSubTitlesFunction = undefined;
      updatedValue.subtitleFunctionString = undefined;
    }

    this._value = updatedValue;

    const serializedValue = this.serializeFunctions(updatedValue);
    this.onChange(serializedValue);
    this.onTouched();
    this.ngModelChange.emit(serializedValue);
  }

  /**
   * Get available field names for function examples
   */
  private getAvailableFieldNames(): string[] {
    if (!this.entityFields || this.entityFields.length === 0) {
      return ['field1', 'field2'];
    }
    return this.entityFields.slice(0, 3).map(field => field.value || field.key || 'field');
  }

  /**
   * Serialize functions for backend storage
   * Converts functions to strings and removes function properties
   */
  private serializeFunctions(config: CardListConfig): CardListConfig {
    const serialized = { ...config };

    if (serialized.getDescriptionFunction && typeof serialized.getDescriptionFunction === 'function') {
      serialized.descriptionFunctionString = serialized.getDescriptionFunction.toString();
      delete serialized.getDescriptionFunction;
    }

    if (serialized.getSubTitlesFunction && typeof serialized.getSubTitlesFunction === 'function') {
      serialized.subtitleFunctionString = serialized.getSubTitlesFunction.toString();
      delete serialized.getSubTitlesFunction;
    }

    if (!serialized.descriptionFunctionString) {
      delete serialized.getDescriptionFunction;
    }
    if (!serialized.subtitleFunctionString) {
      delete serialized.getSubTitlesFunction;
    }

    return serialized;
  }

  /**
   * Restore functions from backend
   * Converts function strings back to executable functions
   */
  private restoreFunctions(config: CardListConfig): CardListConfig {
    const restored = { ...config };

    if (restored.descriptionFunctionString) {
      // eslint-disable-next-line no-eval
      restored.getDescriptionFunction = eval(`(${restored.descriptionFunctionString})`);
    }

    if (restored.subtitleFunctionString) {
      // eslint-disable-next-line no-eval
      restored.getSubTitlesFunction = eval(`(${restored.subtitleFunctionString})`);
    }
    return restored;
  }

  /**
   * Update subtitle row select value property from _value
   */
  private updateSubtitleRowSelectValueProperty() {
    const currentFieldNames = this._value?.subtitleFieldNames || [];
    const result = Array.isArray(currentFieldNames) && currentFieldNames.length > 0
      ? currentFieldNames
          .filter(fieldName => fieldName && typeof fieldName === 'string' && fieldName.trim() !== '')
          .map(fieldName => ({ fieldName: fieldName.trim() }))
      : [];
    this.subtitleRowSelectValueProperty = cloneDeep(result);
  }
}
